import type { TEndpoint, TError } from "@/types";

const apiUtils = {
	composeUrl, // f(apiBaseUrl, endpointConfig, params) => URL
	executeRequest, // f(cb) => [error, response]
};

export default apiUtils;

/**
 * Create URLs for the different APIs
 * @param {string} apiBaseUrl - the API base URL
 * @param {object} endpointConfig - the endpoint path and additional query params { path: string, query: [list of query
 *   params] }. See endpoints.constants.ts
 * @param {object} params - map of all the params (both the path params and query params)
 * @return {string} - the composed URL
 *
 * example:
 *  composeUrl("apiBaseUrl", { path: "/endpoint/{id}", query: ["param1"] }, { param1: "1", id: "2" })
 *  result: "/endpoint/2?param1=1"
 */
export function composeUrl(
	apiBaseUrl: string,
	endpointConfig: TEndpoint,
	params: Record<string, any> = {}
) {
	let url = apiBaseUrl;
	let { path, query } = endpointConfig;

	// add path params (ex: /endpoint/{id})
	Object.keys(params).forEach((param) => {
		path = path.replace(`{${param}}`, String(params[param]));
	});

	// add query params
	if (Array.isArray(query)) {
		const queryParams: String[] = [];
		query.forEach((queryParam) => {
			if (
				typeof params[queryParam] !== "undefined" &&
				params[queryParam] !== null &&
				params[queryParam] !== ""
			) {
				queryParams.push(`${queryParam}=${params[queryParam]}`);
			}
		});

		if (queryParams.length) {
			path += "?" + queryParams.join("&");
		}
	}

	url += path;

	// add a random string to prevent caching the response
	url += url.includes("?") ? "&" : "?";
	url += new Date().getTime();

	return url;
}

/**
 * Executes the provided AJAX request and returns the result / error
 * @param {Function} cb
 */
export async function executeRequest<T>(
	cb: () => Promise<T>
): Promise<[TError | undefined, T | undefined]> {
	try {
		const response: T = await cb();
		return [undefined, response];
	} catch (e: any) {
		return [e as TError, undefined];
	}
}
